/**
 * The entry screen (doc 10).
 *
 * A tenant x app matrix of switches. The whole thing comes from one request —
 * with tens of tenants and a handful of apps, per-cell fetching would be
 * hundreds of requests and a page that visibly assembles itself.
 */
import { Fragment, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Skeleton,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloudSyncOutlinedIcon from '@mui/icons-material/CloudSyncOutlined';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import { useSearchParams } from 'react-router-dom';

import { useLicenseMatrix, useSyncRuns, useSyncUsers, useToggleLicense } from '@/api/hooks';
import { CopyFromProductionDialog } from '@/environment/CopyFromProductionDialog';
import { useEnvironment } from '@/environment/EnvironmentProvider';
import { EnvironmentMismatch } from '@/environment/EnvironmentSwitcher';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { EmptyState, ErrorState, PageHeader } from '@/components/states';
import { SEP_DIVIDER, SEP_GREEN_PALE } from '@/theme';
import type { AppSummary, LicenseCellState, SyncStats, TenantRow } from '@/types/api';
import { useIsMobile } from '@/hooks/useIsMobile';
import { formatDateTime, isExpiringSoon } from './cellState';
import { LicenseCellSwitch } from './LicenseCellSwitch';
import { TenantLicenseCard } from './TenantLicenseCard';
import { TenantUsersPanel } from './TenantUsersPanel';

type StatusFilter = 'all' | 'licensed' | 'unlicensed' | 'expiring' | 'overrides';

interface PendingRevoke {
  tenant: TenantRow;
  app: AppSummary;
  cell: LicenseCellState;
}

const FIRST_COLUMN_WIDTH = 260;

function cellKey(tenantKey: string, appKey: string): string {
  return `${tenantKey}::${appKey}`;
}

/**
 * What to say after an import.
 *
 * "0 neu, 0 aktualisiert, 0 deaktiviert, 0 neue Mandanten" is a poor way to say
 * nothing changed — four zeros read as a failure, and for a while they were one:
 * the counts were arriving under keys this code did not read. An import that
 * genuinely changes nothing is the normal case when the directory is already up
 * to date, so it gets its own sentence, and only the non-zero counts are listed.
 */
function describeSync(stats: SyncStats | null): string {
  if (!stats) return 'Synchronisiert.';

  const parts = [
    stats.usersCreated && `${stats.usersCreated} neu`,
    stats.usersUpdated && `${stats.usersUpdated} aktualisiert`,
    stats.usersDeactivated && `${stats.usersDeactivated} deaktiviert`,
    stats.usersTenantChanged && `${stats.usersTenantChanged} Mandant gewechselt`,
    stats.tenantsCreated && `${stats.tenantsCreated} neue Mandanten`,
    // Counted separately: these users came from the tenant directory and are
    // read-only here, so folding them into "neu" would overstate what the
    // dashboard can actually do with them.
    stats.directoryUsersCreated &&
      `${stats.directoryUsersCreated} weitere aus dem Verzeichnis`,
    stats.directoryTenantsCreated &&
      `${stats.directoryTenantsCreated} neue Mandanten aus dem Verzeichnis`,
  ].filter((part): part is string => Boolean(part));

  const failed = stats.directoryFailedTenants ?? [];
  // Surfaced rather than buried: those tenants' users were deliberately left
  // untouched, so the numbers above are incomplete in a specific, nameable way.
  const warning =
    failed.length > 0
      ? ` ${failed.length} Mandant(en) nicht lesbar, unverändert gelassen: ${failed.slice(0, 5).join(', ')}${failed.length > 5 ? ' …' : ''}.`
      : '';

  if (parts.length === 0) {
    return `Synchronisiert: keine Änderungen — das Verzeichnis war bereits aktuell.${warning}`;
  }
  return `Synchronisiert: ${parts.join(', ')}.${warning}`;
}

export function LicenseAssignmentPage() {
  const [params, setParams] = useSearchParams();
  const isMobile = useIsMobile();
  const { data, isPending, error, refetch } = useLicenseMatrix();
  const { data: syncRuns } = useSyncRuns();
  const toggle = useToggleLicense();
  const sync = useSyncUsers();

  const environment = useEnvironment();
  const [copyOpen, setCopyOpen] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [pendingRevoke, setPendingRevoke] = useState<PendingRevoke | null>(null);
  const [busy, setBusy] = useState<Set<string>>(new Set());
  const [failed, setFailed] = useState<Set<string>>(new Set());
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(
    null,
  );

  // Filter state lives in the URL so a view can be sent to a colleague.
  const search = params.get('q') ?? '';
  const appFilter = params.get('app') ?? '';
  const statusFilter = (params.get('status') as StatusFilter | null) ?? 'all';

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const visibleTenants = useMemo(() => {
    if (!data) return [];
    const needle = search.trim().toLowerCase();
    return data.tenants.filter((tenant) => {
      if (
        needle &&
        !tenant.key.toLowerCase().includes(needle) &&
        !tenant.displayName.toLowerCase().includes(needle)
      ) {
        return false;
      }
      if (statusFilter === 'all') return true;

      const relevant = appFilter
        ? [tenant.licenses[appFilter] ?? null]
        : data.apps.map((app) => tenant.licenses[app.key] ?? null);

      switch (statusFilter) {
        case 'licensed':
          return relevant.some((cell) => cell?.isActive);
        case 'unlicensed':
          return relevant.every((cell) => !cell?.isActive);
        case 'expiring':
          return relevant.some((cell) => isExpiringSoon(cell));
        case 'overrides':
          return relevant.some(
            (cell) => (cell?.overrideCount.allow ?? 0) + (cell?.overrideCount.deny ?? 0) > 0,
          );
        default:
          return true;
      }
    });
  }, [data, search, statusFilter, appFilter]);

  const visibleApps = useMemo(() => {
    if (!data) return [];
    return appFilter ? data.apps.filter((app) => app.key === appFilter) : data.apps;
  }, [data, appFilter]);

  const markBusy = (key: string, on: boolean) =>
    setBusy((current) => {
      const next = new Set(current);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });

  const flashFailure = (key: string) => {
    setFailed((current) => new Set(current).add(key));
    window.setTimeout(
      () =>
        setFailed((current) => {
          const next = new Set(current);
          next.delete(key);
          return next;
        }),
      2000,
    );
  };

  const applyToggle = (
    tenant: TenantRow,
    app: AppSummary,
    cell: LicenseCellState,
    enabled: boolean,
    notes?: string,
  ) => {
    const key = cellKey(tenant.key, app.key);
    markBusy(key, true);
    toggle.mutate(
      {
        tenantKey: tenant.key,
        appKey: app.key,
        enabled,
        expectedUpdatedAt: cell?.updatedAt ?? null,
        notes: notes ?? null,
      },
      {
        onSuccess: () => {
          setToast({
            message: `${app.name} für ${tenant.displayName} ${enabled ? 'aktiviert' : 'deaktiviert'}.`,
            severity: 'success',
          });
        },
        onError: (mutationError) => {
          flashFailure(key);
          setToast({
            message: mutationError.isConflict
              ? `${app.name} / ${tenant.displayName}: ${mutationError.message}`
              : `${app.name} für ${tenant.displayName} konnte nicht geändert werden: ${mutationError.message}`,
            severity: 'error',
          });
          // A 409 means someone else got there first: take their version rather
          // than quietly applying this click on top of their decision.
          if (mutationError.isConflict) void refetch();
        },
        onSettled: () => markBusy(key, false),
      },
    );
  };

  const handleToggle = (tenant: TenantRow, app: AppSummary, enabled: boolean) => {
    const cell = tenant.licenses[app.key] ?? null;
    // Granting is safe and reversible; revoking removes a paying customer's
    // access, so only that direction confirms.
    if (!enabled) {
      setPendingRevoke({ tenant, app, cell });
      return;
    }
    applyToggle(tenant, app, cell, true);
  };

  const lastSync = syncRuns?.[0];

  return (
    <Box>
      <PageHeader
        title="Lizenzvergabe"
        description="Steuert, welche Mandanten Zugriff auf welche Apps haben."
        actions={
          <Stack spacing={0.5} sx={{ alignItems: { xs: 'stretch', sm: 'flex-end' }, width: { xs: '100%', sm: 'auto' } }}>
            <Button
              variant="outlined"
              startIcon={<SyncOutlinedIcon />}
              disabled={sync.isPending}
              onClick={() =>
                sync.mutate(undefined, {
                  onSuccess: (run) => {
                    setToast({ message: describeSync(run.statsJson), severity: 'success' });
                  },
                  onError: (syncError) =>
                    setToast({ message: syncError.message, severity: 'error' }),
                })
              }
            >
              {sync.isPending ? 'Synchronisiert…' : 'Mit SEP synchronisieren'}
            </Button>
            {/* Only offered where it can do something. Production is not a copy
                target, and showing the button there greyed out would invite the
                question "why not?" on every visit. */}
            {environment !== 'production' && (
              <Button
                variant="outlined"
                color="warning"
                startIcon={<CloudSyncOutlinedIcon />}
                onClick={() => setCopyOpen(true)}
              >
                Mit Production synchronisieren
              </Button>
            )}
            {lastSync && (
              <Typography variant="caption" color="text.secondary">
                Zuletzt: {formatDateTime(lastSync.finishedAt ?? lastSync.startedAt)}
                {lastSync.status === 'failed' && ' · fehlgeschlagen'}
              </Typography>
            )}
          </Stack>
        }
      />

      <CopyFromProductionDialog open={copyOpen} onClose={() => setCopyOpen(false)} />

      {error && <ErrorState error={error} onRetry={() => void refetch()} />}

      {/* The backend echoes which environment it answered for. If that is not
          the one selected, a request outlived a switch and these rows belong to
          somewhere else — say so instead of drawing them under the wrong
          banner. */}
      {data && data.environment !== environment && (
        <Box sx={{ mb: 2 }}>
          <EnvironmentMismatch answered={data.environment} />
        </Box>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          placeholder="Mandant suchen…"
          value={search}
          onChange={(event) => setParam('q', event.target.value)}
          sx={{ width: { xs: '100%', md: 280 } }}
        />
        <TextField
          select
          label="App"
          value={appFilter}
          onChange={(event) => setParam('app', event.target.value)}
          sx={{ width: { xs: '100%', md: 220 } }}
        >
          <MenuItem value="">Alle Apps</MenuItem>
          {data?.apps.map((app) => (
            <MenuItem key={app.key} value={app.key}>
              {app.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Status"
          value={statusFilter}
          onChange={(event) => setParam('status', event.target.value)}
          sx={{ width: { xs: '100%', md: 220 } }}
        >
          <MenuItem value="all">Alle</MenuItem>
          <MenuItem value="licensed">Mit Zugriff</MenuItem>
          <MenuItem value="unlicensed">Ohne Zugriff</MenuItem>
          <MenuItem value="expiring">Läuft bald ab</MenuItem>
          <MenuItem value="overrides">Nur mit Ausnahmen</MenuItem>
        </TextField>
      </Stack>

      {isPending ? (
        <Stack spacing={1}>
          {Array.from({ length: 8 }, (_, index) => (
            <Skeleton key={index} variant="rectangular" height={40} />
          ))}
        </Stack>
      ) : !data || data.tenants.length === 0 ? (
        <EmptyState
          title="Noch keine Mandanten"
          description="Synchronisieren Sie mit SEP, um Mandanten und Benutzer zu laden."
          action={
            <Button variant="contained" onClick={() => sync.mutate()}>
              Mit SEP synchronisieren
            </Button>
          }
        />
      ) : visibleTenants.length === 0 ? (
        <EmptyState
          title="Keine Treffer"
          description="Kein Mandant entspricht den aktuellen Filtern."
          action={<Button onClick={() => setParams(new URLSearchParams())}>Filter zurücksetzen</Button>}
        />
      ) : isMobile ? (
        // One card per tenant. See TenantLicenseCard for why the grid is
        // rotated rather than scrolled at this width.
        <Stack spacing={1.5}>
          {visibleTenants.map((tenant) => (
            <TenantLicenseCard
              key={tenant.key}
              tenant={tenant}
              apps={visibleApps}
              expanded={expanded === tenant.key}
              busy={busy}
              failed={failed}
              onToggleExpand={() =>
                setExpanded((current) => (current === tenant.key ? null : tenant.key))
              }
              onToggle={(app, next) => handleToggle(tenant, app, next)}
            >
              <TenantUsersPanel tenantKey={tenant.key} apps={data.apps} expanded />
            </TenantLicenseCard>
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '70vh' }}>
          <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', minWidth: 720 }}>
            <TableHead>
              <TableRow>
                {/* Sticky both ways: the first column has to survive horizontal
                    scrolling or the switches lose their row label. */}
                <TableCell
                  sx={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 3,
                    width: FIRST_COLUMN_WIDTH,
                    backgroundColor: '#FAFAFA',
                    borderRight: `1px solid ${SEP_DIVIDER}`,
                  }}
                >
                  Mandant
                </TableCell>
                {visibleApps.map((app) => (
                  <TableCell key={app.key} align="center" sx={{ width: 120 }}>
                    <Tooltip title={app.description ?? ''}>
                      <Stack spacing={0.25} sx={{ alignItems: 'center' }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: app.isEnabled ? 'text.primary' : 'text.disabled',
                          }}
                        >
                          {app.name}
                        </Typography>
                        {!app.isEnabled && <Chip label="bald" variant="outlined" />}
                      </Stack>
                    </Tooltip>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleTenants.map((tenant) => (
                <Fragment key={tenant.key}>
                  <TableRow hover>
                    <TableCell
                      sx={{
                        position: 'sticky',
                        left: 0,
                        zIndex: 1,
                        backgroundColor: expanded === tenant.key ? SEP_GREEN_PALE : '#fff',
                        borderRight: `1px solid ${SEP_DIVIDER}`,
                      }}
                    >
                      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                        <IconButton
                          size="small"
                          aria-label={`Benutzer von ${tenant.displayName} ${expanded === tenant.key ? 'ausblenden' : 'anzeigen'}`}
                          onClick={() =>
                            setExpanded((current) => (current === tenant.key ? null : tenant.key))
                          }
                        >
                          {expanded === tenant.key ? (
                            <ExpandLessIcon fontSize="small" />
                          ) : (
                            <ExpandMoreIcon fontSize="small" />
                          )}
                        </IconButton>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                            {tenant.displayName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {tenant.key} · {tenant.userCount} Benutzer
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    {visibleApps.map((app) => (
                      <TableCell key={app.key} align="center" sx={{ py: 0.25 }}>
                        <LicenseCellSwitch
                          app={app}
                          tenantName={tenant.displayName}
                          cell={tenant.licenses[app.key] ?? null}
                          saving={busy.has(cellKey(tenant.key, app.key))}
                          failed={failed.has(cellKey(tenant.key, app.key))}
                          onToggle={(next) => handleToggle(tenant, app, next)}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                  {expanded === tenant.key && (
                    <TableRow>
                      <TableCell colSpan={visibleApps.length + 1} sx={{ p: 0 }}>
                        <TenantUsersPanel
                          tenantKey={tenant.key}
                          apps={data.apps}
                          expanded
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Stack direction="row" spacing={2} sx={{ mt: 1.5, alignItems: 'center' }}>
        <FormControlLabel
          control={<Switch size="small" checked disabled />}
          label={<Typography variant="caption">Zugriff erteilt</Typography>}
        />
        <Typography variant="caption" color="text.secondary">
          Zahl am Schalter = Benutzer-Ausnahmen (blau erlaubt, rot gesperrt)
        </Typography>
      </Stack>

      <ConfirmDialog
        open={pendingRevoke !== null}
        title="Zugriff entziehen?"
        destructive
        withNote
        confirmLabel="Zugriff entziehen"
        message={
          pendingRevoke && (
            <>
              <strong>{pendingRevoke.tenant.displayName}</strong> verliert den Zugriff auf{' '}
              <strong>{pendingRevoke.app.name}</strong>. {pendingRevoke.tenant.userCount} Benutzer
              sind betroffen.
            </>
          )
        }
        onCancel={() => setPendingRevoke(null)}
        onConfirm={(note) => {
          if (pendingRevoke) {
            applyToggle(
              pendingRevoke.tenant,
              pendingRevoke.app,
              pendingRevoke.cell,
              false,
              note || undefined,
            );
          }
          setPendingRevoke(null);
        }}
      />

      <Snackbar
        open={toast !== null}
        autoHideDuration={toast?.severity === 'error' ? 8000 : 4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          severity={toast?.severity ?? 'success'}
          variant="filled"
          onClose={() => setToast(null)}
        >
          {toast?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
